import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { HeaderActionsContext } from 'widgets/Header/context/headerActionsContext'
import { useHeaderActions } from 'shared/hooks/useHeaderActions'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { ensureStudentsLoaded } from 'pages/Students/model/ensureStudentsLoaded'
import { researchSessionStore } from 'shared/stores/researchSessionStore'
import { courseMapStore } from 'shared/stores/courseMapStore'
import { deriveDegreeFields } from 'shared/utils/normalizeAiToolsProfile'
import {
	buildJourneySelectionPayload,
	shouldCreateJourney,
} from 'pages/Research/utils/buildJourneySelectionPayload'
import { getLastSelectionFinder } from 'pages/Research/utils/journeyResumeUtils'
import { reconcileCountrySelection } from '../utils/researchSelectionValidation'
import { removeEmojiPrefix } from 'shared/utils/removeEmojiPrefix'
import {
	getCourseMapCategoryLabel,
	getCourseMapSubCategories,
	getCourseMapSubCategoryLabel,
} from 'shared/utils/courseMapLookup'
import HeaderStudentMenu from '../components/HeaderStudentMenu/HeaderStudentMenu'
import headerStyles from '../components/AiToolContainer/styles/AiToolContainer.module.scss'
import { ReactComponent as ArrowBackIcon } from 'assets/icons/arrow_back.svg'
import { useResearchCareer } from '../components/CareerFinder/hooks/useResearchCareer'
import { useResearchCountry } from '../components/CountryFinder/hooks/useResearchCountry'
import { useResearchCourse } from '../components/CourseFinder/hooks/useResearchCourse'
import { useResearchUniversity } from '../components/UniversityFinder/hooks/useResearchUniversity'
import { TOOLS, getStudentName } from '../constants/researchFixtures'
import { sendMessagesToStudents } from 'api/all'
import {
	buildRecommendationMessageText,
	buildRecommendationMetadata,
	getUniversityRecommendationLabel,
} from '../utils/buildRecommendationMessage'
import { getResearchUniversitySelectionId } from '../utils/studentUniversityLinks'

const FINDER_TITLES = {
	main: 'Research',
	career: 'Career Finder',
	country: 'Country Finder',
	course: 'Course Finder',
	college: 'Colleges',
}

export function useResearchPage() {
	const { setHeaderLeading } = useContext(HeaderActionsContext)
	const [activeScreen, setActiveScreen] = useState('main')
	const [isSendOpen, setIsSendOpen] = useState(false)
	const [sendStep, setSendStep] = useState('student')
	const [pickerOrigin, setPickerOrigin] = useState('tray')
	const [categoryPickerOpen, setCategoryPickerOpen] = useState(false)
	const [categoryPickerStep, setCategoryPickerStep] = useState('category')
	const [selectedStudentId, setSelectedStudentId] = useState(
		() => researchSessionStore.selectedStudentId || '',
	)
	const [pendingStudentId, setPendingStudentId] = useState('')
	const [studentSearch, setStudentSearch] = useState('')
	const [studentPickerGrade, setStudentPickerGrade] = useState('all')
	const [pendingSwitchStudentId, setPendingSwitchStudentId] = useState(null)

	const [isSelectedCareer, setIsSelectedCareer] = useState(false)
	const [isSelectedCountry, setIsSelectedCountry] = useState(false)
	const [isSelectedCourse, setIsSelectedCourse] = useState(false)
	const [selectedCourses, setSelectedCourses] = useState([])
	const [selectedUniversities, setSelectedUniversities] = useState([])
	const [numCareerTitle, setNumCareerTitle] = useState(null)
	const [selectionPending, setSelectionPending] = useState(false)
	const [pendingSelectionId, setPendingSelectionId] = useState(null)
	const selectionLockRef = useRef(false)
	const countriesLoadRef = useRef({ career: '', wasLoading: true })

	const draftCategory = researchSessionStore.draftDefaults?.category || ''
	const draftSubCategory = researchSessionStore.draftDefaults?.subCategory || ''
	const draftCareer = researchSessionStore.draftDefaults?.career || ''
	const rawDraftCountries = researchSessionStore.draftDefaults?.countries
	const draftCountries = useMemo(
		() => (Array.isArray(rawDraftCountries) ? rawDraftCountries : []),
		[rawDraftCountries],
	)
	const draftCountry =
		researchSessionStore.draftDefaults?.country || draftCountries[0] || ''
	const draftCourse = researchSessionStore.draftDefaults?.course || ''
	const draftHighestLevel =
		researchSessionStore.draftDefaults?.highestLevel || 'HighSchool'
	const subCategory = draftSubCategory
	const career = draftCareer

	const activeDegree = useMemo(() => {
		const derived = deriveDegreeFields(
			{ highestLevel: draftHighestLevel },
			{ isStudent: false },
		)
		return derived.activeDegree || 'Bachelors'
	}, [draftHighestLevel])

	const loadingDetails =
		researchSessionStore.selectedStudentId == null
			? researchSessionStore.personalStatus === 'idle' ||
				researchSessionStore.personalStatus === 'loading'
			: researchSessionStore.sourcesStatus === 'idle' ||
				researchSessionStore.sourcesStatus === 'loading'

	const sourcesSettled =
		researchSessionStore.selectedStudentId == null
			? researchSessionStore.personalStatus === 'success' ||
				researchSessionStore.personalStatus === 'error'
			: researchSessionStore.sourcesStatus === 'success' ||
				researchSessionStore.sourcesStatus === 'error'

	const courseMap = courseMapStore.courseMap
	const courseMapLoading = courseMapStore.loading
	const courseMapError = courseMapStore.error
	const sessionSelectedStudentId = researchSessionStore.selectedStudentId

	const categoryDisplayLabel =
		getCourseMapCategoryLabel(courseMap, draftCategory) || draftCategory
	const subCategoryDisplayLabel =
		getCourseMapSubCategoryLabel(courseMap, draftCategory, draftSubCategory) ||
		draftSubCategory

	const awaitingCatalogDefaults =
		!courseMapError &&
		!draftSubCategory &&
		(loadingDetails ||
			courseMapLoading ||
			(sourcesSettled && Object.keys(courseMap || {}).length > 0))

	const levels = useMemo(
		() => Object.keys(courseMap || {}),
		[courseMap],
	)
	const subCategories = useMemo(
		() => getCourseMapSubCategories(courseMap, draftCategory),
		[courseMap, draftCategory],
	)
	const loadingCategory = courseMapLoading

	const careerState = useResearchCareer({
		activeScreen,
		subCategory,
		career,
		activeDegree,
		loadingDetails,
		awaitingCatalogDefaults,
		isSelectedCareer,
		setIsSelectedCareer,
		numCareerTitle,
		setNumCareerTitle,
	})

	const countryState = useResearchCountry({
		career,
		subCategory,
		draftCategory,
		draftSubCategory,
		draftCountry,
		draftCountries,
		activeDegree,
		loadingDetails,
		loadingProfession: careerState.loadingProfession,
		awaitingCatalogDefaults,
		professionsError: careerState.professionsError,
		professionsLength: careerState.professions.length,
		isValidCareer: careerState.isValidCareer,
		isSelectedCountry,
		setIsSelectedCountry,
		sourcesSettled,
		courseMapError: courseMapStore.error,
	})

	const courseState = useResearchCourse({
		career,
		draftCategory,
		draftSubCategory,
		draftCountry,
		draftCourse,
		activeDegree,
		levels,
		subCategories,
		loadingDetails,
		loadingCategory,
		loadingCountries: countryState.loadingCountries,
		isValidCareer: careerState.isValidCareer,
		isSelectedCountry,
		isSelectedCourse,
		selectedCourses,
		setIsSelectedCourse,
		setSelectedCourses,
	})

	const universityState = useResearchUniversity({
		selectedStudentId,
		activeScreen,
		hasFinderSelection:
			isSelectedCareer || isSelectedCountry || isSelectedCourse,
		hasSelectedCourse: isSelectedCourse,
	})

	const clearDependentSelection = useCallback(() => {
		countryState.setRecCountry(null)
		setIsSelectedCountry(false)
		setIsSelectedCareer(false)
		setIsSelectedCourse(false)
		setSelectedCourses([])
		setNumCareerTitle(null)
	}, [countryState])

	const loadingCountries = countryState.loadingCountries
	const availableCountries = countryState.countries
	const setRecCountry = countryState.setRecCountry
	const isValidCountry = countryState.isValidCountry
	const resetCourses = courseState.resetCourses

	// After career change + countries catalog load: validate selected countries and draft country.
	useEffect(() => {
		const wasLoading = countriesLoadRef.current.wasLoading
		const careerChanged = countriesLoadRef.current.career !== career
		countriesLoadRef.current = {
			career,
			wasLoading: loadingCountries,
		}

		if (loadingCountries) return
		if (!availableCountries?.length) return
		if (!wasLoading && !careerChanged) return

		const result = reconcileCountrySelection({
			draftCountry,
			selectedCountries: draftCountries,
			availableCountries,
			isSelectedCountry,
		})

		if (result.isSelectedCountry !== isSelectedCountry) {
			setIsSelectedCountry(result.isSelectedCountry)
		}

		if (result.draftChanged) {
			researchSessionStore.updateDraft({ country: result.draftCountry })
			resetCourses()
		}

		const index = availableCountries.findIndex(
			el => el.country === result.draftCountry,
		)
		setRecCountry(index >= 0 ? index : null)
		isValidCountry.current = true
	}, [
		career,
		loadingCountries,
		availableCountries,
		draftCountry,
		draftCountries,
		isSelectedCountry,
		setRecCountry,
		resetCourses,
		isValidCountry,
	])

	// Empty session: first category + first subcategory after sources settle
	useEffect(() => {
		if (!sourcesSettled) return
		if (courseMapLoading || courseMapError) return

		const courseMapLevels = Object.keys(courseMap || {})
		if (!courseMapLevels.length) return

		const categoryForSubs =
			draftCategory || removeEmojiPrefix(courseMapLevels[0]) || courseMapLevels[0]
		const firstCategory =
			draftCategory || removeEmojiPrefix(courseMapLevels[0]) || courseMapLevels[0]
		const subs = getCourseMapSubCategories(courseMap, categoryForSubs)
		if (!subs.length) return
		const firstSubCategory =
			draftSubCategory || removeEmojiPrefix(subs[0]) || subs[0]

		if (draftCategory && draftSubCategory) return

		researchSessionStore.seedDraftIfEmpty({
			category: firstCategory,
			subCategory: firstSubCategory,
		})
	}, [
		sourcesSettled,
		courseMap,
		courseMapLoading,
		courseMapError,
		sessionSelectedStudentId,
		draftCategory,
		draftSubCategory,
	])

	const applySelectedStudentId = useCallback((studentId, { force = true } = {}) => {
		const normalized = studentId ? String(studentId) : ''
		// Force switch: dirty-draft confirm modal is temporarily disabled.
		researchSessionStore.selectStudent(normalized || null, { force })
		setPendingSwitchStudentId(null)
		setSelectedStudentId(normalized)
		return true
	}, [])

	const keepCurrentResearchOnSwitch = useCallback(() => {
		setPendingSwitchStudentId(null)
	}, [])

	const loadPendingStudentOnSwitch = useCallback(() => {
		const pendingId = pendingSwitchStudentId
		setPendingSwitchStudentId(null)
		applySelectedStudentId(pendingId, { force: true })
	}, [pendingSwitchStudentId, applySelectedStudentId])

	const openCategoryPicker = useCallback((step = 'category') => {
		setCategoryPickerStep(step)
		setCategoryPickerOpen(true)
	}, [])

	const closeCategoryPicker = useCallback(() => {
		setCategoryPickerOpen(false)
	}, [])

	const onCategoryOrSubCategoryCommitted = useCallback(
		({ categoryChanged, subCategoryChanged }) => {
			if (!categoryChanged && !subCategoryChanged) return
			careerState.resetProfessions()
			countryState.resetCountries()
			courseState.resetCourses()
			clearDependentSelection()
		},
		[careerState, countryState, courseState, clearDependentSelection],
	)

	const persistFinderSelection = useCallback(
		async (finder, profilePartial, selectionState = {}) => {
			const profileResult =
				await researchSessionStore.commitProfile(profilePartial)
			if (!profileResult.ok) return profileResult

			const existingJourney = researchSessionStore.getActiveJourney()
			const journeyPayload = buildJourneySelectionPayload(finder, {
				draft: researchSessionStore.draftDefaults,
				typeFlow: researchSessionStore.typeFlow,
				activeJourneyId: researchSessionStore.activeJourneyId,
				existingJourney,
				...selectionState,
			})

			if (researchSessionStore.activeJourneyId) {
				return researchSessionStore.patchActiveJourney(journeyPayload)
			}

			if (
				shouldCreateJourney({
					activeJourneyId: researchSessionStore.activeJourneyId,
					pendingMainCardJourney: researchSessionStore.pendingMainCardJourney,
				})
			) {
				return researchSessionStore.createJourneyFromProfile(journeyPayload)
			}

			return profileResult
		},
		[],
	)

	const careerTitlesFromIds = useCallback(
		ids =>
			ids
				.map(id => careerState.careers.find(item => item.id === id)?.title)
				.filter(Boolean),
		[careerState.careers],
	)

	const applyCareerSelectionState = useCallback(
		(nextCareer, nextIds, markSelected) => {
			if (markSelected) {
				setIsSelectedCareer(nextIds.length > 0)
			}
			const index = careerState.professions.findIndex(
				el => el.profession_title === nextCareer,
			)
			setNumCareerTitle(index >= 0 ? index : 0)
			careerState.isValidCareer.current = true
		},
		[careerState.professions, careerState.isValidCareer],
	)

	const selectDraftCareer = useCallback(
		async (nextCareer, { markSelected = false } = {}) => {
			if (!nextCareer) return

			if (nextCareer === career) {
				if (markSelected) setIsSelectedCareer(true)
				const index = careerState.professions.findIndex(
					el => el.profession_title === nextCareer,
				)
				if (index >= 0) setNumCareerTitle(index)
				return
			}

			countryState.resetCountries()
			courseState.resetCourses()
			countryState.setRecCountry(null)
			if (!markSelected) {
				careerState.replaceSelectedIds([])
				setIsSelectedCountry(false)
				setIsSelectedCourse(false)
				setSelectedCourses([])
			}

			if (!markSelected) {
				await researchSessionStore.commitProfile({
					career: nextCareer,
					course: '',
				})
				const index = careerState.professions.findIndex(
					el => el.profession_title === nextCareer,
				)
				setNumCareerTitle(index >= 0 ? index : 0)
				careerState.isValidCareer.current = true
				return
			}

			const nextIds = [careerState.careers.find(c => c.title === nextCareer)?.id]
				.filter(Boolean)
			careerState.replaceSelectedIds(nextIds)

			if (nextCareer !== career) {
				countryState.resetCountries()
				courseState.resetCourses()
			}

			await persistFinderSelection(
				'career',
				{
					career: nextCareer,
				},
				{
					nextCareer,
					nextCareers: [nextCareer],
					isSelectedCareer: true,
				},
			)

			applyCareerSelectionState(nextCareer, nextIds, true)
		},
		[
			career,
			careerState,
			countryState,
			courseState,
			persistFinderSelection,
			applyCareerSelectionState,
		],
	)

	const withSelectionLock = useCallback(async (selectionId, work) => {
		if (selectionLockRef.current) return
		selectionLockRef.current = true
		setSelectionPending(true)
		setPendingSelectionId(selectionId || null)
		try {
			await work()
		} finally {
			selectionLockRef.current = false
			setSelectionPending(false)
			setPendingSelectionId(null)
		}
	}, [])

	const selectDraftCareerFromFilter = useCallback(
		async nextCareer => {
			if (!nextCareer || selectionLockRef.current) return
			await withSelectionLock(`career-filter:${nextCareer}`, () =>
				selectDraftCareer(nextCareer, { markSelected: false }),
			)
		},
		[withSelectionLock, selectDraftCareer],
	)

	// Career card multi-select + draft career used for dependent finders.
	// First select (isSelectedCareer=false): replace default with this career.
	// Later selects: add/remove from list; newly added becomes active draft for API data.
	const toggleCareerCard = useCallback(
		async careerId => {
			if (!careerId || selectionLockRef.current) return
			const careerItem = careerState.careers.find(item => item.id === careerId)
			if (!careerItem?.title) return

			await withSelectionLock(careerId, async () => {
				let nextIds
				if (!isSelectedCareer) {
					nextIds = [careerId]
				} else if (careerState.selectedIds.includes(careerId)) {
					nextIds = careerState.selectedIds.filter(id => id !== careerId)
				} else {
					nextIds = [...careerState.selectedIds, careerId]
				}

				careerState.replaceSelectedIds(nextIds)
				const nextCareers = careerTitlesFromIds(nextIds)
				const nextCareer = nextCareers.at(-1) || careerItem.title

				if (!nextIds.length) {
					setIsSelectedCareer(false)
					await persistFinderSelection(
						'career',
						{ career: nextCareer || career },
						{
							nextCareer: nextCareer || career,
							nextCareers: [],
							isSelectedCareer: false,
						},
					)
					return
				}

				if (nextCareer !== career) {
					countryState.resetCountries()
					courseState.resetCourses()
				}

				await persistFinderSelection(
					'career',
					{
						career: nextCareer,
					},
					{
						nextCareer,
						nextCareers,
						isSelectedCareer: true,
					},
				)

				applyCareerSelectionState(nextCareer, nextIds, true)
			})
		},
		[
			withSelectionLock,
			isSelectedCareer,
			career,
			careerState,
			countryState,
			courseState,
			careerTitlesFromIds,
			persistFinderSelection,
			applyCareerSelectionState,
		],
	)

	// Countries: first explicit select replaces default; later toggles multi-select.
	const toggleDraftCountry = useCallback(
		async countryTitle => {
			if (!countryTitle || selectionLockRef.current) return

			await withSelectionLock(countryTitle, async () => {
				const current = Array.isArray(
					researchSessionStore.draftDefaults?.countries,
				)
					? researchSessionStore.draftDefaults.countries
					: []
				const persistedCountry =
					researchSessionStore.draftDefaults?.country || draftCountry || ''

				let nextCountries
				if (!isSelectedCountry) {
					nextCountries = [countryTitle]
				} else if (current.includes(countryTitle)) {
					nextCountries = current.filter(item => item !== countryTitle)
				} else {
					nextCountries = [...current, countryTitle]
				}

				const lastCountry = nextCountries.length
					? nextCountries[nextCountries.length - 1]
					: persistedCountry

				if (lastCountry !== draftCountry) {
					courseState.resetCourses()
				}

				await persistFinderSelection(
					'country',
					{
						country: lastCountry,
						countries: nextCountries,
					},
					{
						nextCountry: lastCountry,
						nextCountries,
						nextCareer: researchSessionStore.draftDefaults?.career || career,
						nextCareers: careerTitlesFromIds(careerState.selectedIds),
						isSelectedCareer,
					},
				)

				setIsSelectedCountry(nextCountries.length > 0)
				const index = countryState.countries.findIndex(
					el => el.country === lastCountry,
				)
				countryState.setRecCountry(index >= 0 ? index : null)
				countryState.isValidCountry.current = true
			})
		},
		[
			withSelectionLock,
			isSelectedCountry,
			draftCountry,
			countryState,
			courseState,
			persistFinderSelection,
			career,
			isSelectedCareer,
			careerState,
			careerTitlesFromIds,
		],
	)

	// Courses: first explicit select replaces default; later toggles multi-select.
	const toggleDraftCourse = useCallback(
		async courseTitle => {
			if (!courseTitle || selectionLockRef.current) return

			await withSelectionLock(courseTitle, async () => {
				let next
				if (!isSelectedCourse) {
					next = [courseTitle]
				} else if (selectedCourses.includes(courseTitle)) {
					next = selectedCourses.filter(item => item !== courseTitle)
				} else {
					next = [...selectedCourses, courseTitle]
				}

				setSelectedCourses(next)
				const lastCourse = next.length
					? next[next.length - 1]
					: draftCourse || ''
				await persistFinderSelection(
					'course',
					{ course: lastCourse },
					{
						nextCourse: lastCourse,
						nextCourses: next,
						nextCareer: researchSessionStore.draftDefaults?.career || career,
						nextCareers: careerTitlesFromIds(careerState.selectedIds),
						nextCountry:
							researchSessionStore.draftDefaults?.country || draftCountry,
						nextCountries: researchSessionStore.draftDefaults?.countries || [],
						isSelectedCareer,
					},
				)
				setIsSelectedCourse(next.length > 0)
				courseState.isValidCourse.current = true
			})
		},
		[
			withSelectionLock,
			isSelectedCourse,
			selectedCourses,
			draftCourse,
			courseState,
			persistFinderSelection,
			career,
			draftCountry,
			isSelectedCareer,
			careerState,
			careerTitlesFromIds,
		],
	)

	const selectDraftDegree = useCallback(
		async newDegree => {
			if (!newDegree || newDegree === activeDegree || selectionLockRef.current)
				return
			await withSelectionLock(`degree:${newDegree}`, async () => {
				courseState.resetCourses()
				setIsSelectedCourse(false)
				setSelectedCourses([])
				const highestLevel =
					newDegree === 'Bachelors' ? 'HighSchool' : 'Bachelors'
				await researchSessionStore.commitProfile({
					highestLevel,
					course: '',
				})
			})
		},
		[
			activeDegree,
			withSelectionLock,
			courseState,
		],
	)

	const retryCourseMap = useCallback(() => {
		if (courseMapStore.loading) return
		courseMapStore.ensureLoaded(activeDegree, { force: true }).catch(() => {})
	}, [activeDegree])

	const onExploreSelect = useCallback(toolId => {
		if (
			toolId === 'career' ||
			toolId === 'country' ||
			toolId === 'course' ||
			toolId === 'college'
		) {
			researchSessionStore.beginMainCardJourney()
			if (toolId !== 'college') {
				researchSessionStore.setTypeFlow(toolId)
			}
			setActiveScreen(toolId)
		}
	}, [])

	const onStepperSelect = useCallback(stepId => {
		if (selectionLockRef.current) return
		researchSessionStore.setEntryOrigin('stepper')
		setActiveScreen(stepId)
	}, [])

	const onResumeJourney = useCallback(
		async journey => {
			const result = await researchSessionStore.resumeJourney(journey)
			if (!result.ok) return

			const { resume } = result
			setIsSelectedCareer(resume.isSelectedCareer)
			setIsSelectedCountry(resume.isSelectedCountry)
			setIsSelectedCourse(resume.isSelectedCourse)
			setSelectedCourses(resume.courses)

			if (resume.careers.length) {
				const ids = resume.careers
					.map(title =>
						careerState.careers.find(item => item.title === title)?.id,
					)
					.filter(Boolean)
				careerState.replaceSelectedIds(ids)
			} else {
				careerState.replaceSelectedIds([])
			}

			researchSessionStore.setTypeFlow(journey.typeFlowJourney || 'career')
			setActiveScreen(getLastSelectionFinder(journey))
		},
		[careerState],
	)

	const onViewStudentJourney = useCallback(async journey => {
		const result =
			await researchSessionStore.applyStudentJourneyAsDefaults(journey)
		if (!result.ok) return
		const flow = journey.typeFlowJourney || 'career'
		researchSessionStore.setTypeFlow(flow)
		if (journey.course) {
			setActiveScreen('college')
			return
		}
		const countries = Array.isArray(journey.countries)
			? journey.countries
			: typeof journey.countries === 'string' && journey.countries
				? [journey.countries]
				: []
		if (journey.career || countries.length) {
			setActiveScreen(countries.length && !journey.career ? 'career' : 'country')
			return
		}
		setActiveScreen(
			['career', 'country', 'course', 'college'].includes(flow)
				? flow
				: 'career',
		)
	}, [])

	const onDeleteJourney = useCallback(async journeyId => {
		await researchSessionStore.deleteJourney(journeyId)
	}, [])

	const storeStudents = studentsStore.students
	const students = useMemo(() => {
		return Array.isArray(storeStudents)
			? storeStudents.map(student => ({
					...student,
					id: String(student.id || student.user_id || student._id),
					name: getStudentName(student),
					meta: student.grade ? `Grade ${student.grade}` : 'Kandor student',
				}))
			: []
	}, [storeStudents])

	const studentGradeTabs = useMemo(() => {
		const gradeCounts = {}
		students.forEach(student => {
			const grade = Number(student?.grade)
			if (grade && !Number.isNaN(grade) && grade > 0) {
				gradeCounts[grade] = (gradeCounts[grade] || 0) + 1
			}
		})
		const gradeTabs = Object.entries(gradeCounts)
			.sort((a, b) => Number(a[0]) - Number(b[0]))
			.map(([grade, count]) => ({
				id: `grade-${grade}`,
				label: `Grade ${grade}`,
				count,
			}))
		return [
			{ id: 'all', label: 'All Students', count: students.length },
			...gradeTabs,
		]
	}, [students])

	const selectedStudent = students.find(
		student => student.id === selectedStudentId,
	)
	const pendingStudent = students.find(
		student => student.id === pendingStudentId,
	)
	const selectedStudentName = selectedStudent
		? getStudentName(selectedStudent)
		: ''
	const pendingSwitchStudent = students.find(
		student => student.id === pendingSwitchStudentId,
	)
	const pendingSwitchStudentName = pendingSwitchStudent
		? getStudentName(pendingSwitchStudent)
		: pendingSwitchStudentId
			? 'selected student'
			: ''
	const recentStudentsKey = JSON.stringify(
		students.slice(0, 4).map(student => ({
			id: student.id,
			name: getStudentName(student),
			meta: student.meta,
		})),
	)
	const studentQuery = studentSearch.trim().toLowerCase()
	const visibleStudents = useMemo(() => {
		const gradeNum =
			studentPickerGrade &&
			studentPickerGrade !== 'all' &&
			typeof studentPickerGrade === 'string' &&
			studentPickerGrade.startsWith('grade-')
				? Number(studentPickerGrade.replace('grade-', ''))
				: null

		let filtered = students
		if (gradeNum != null && !Number.isNaN(gradeNum)) {
			filtered = filtered.filter(
				student => Number(student.grade) === gradeNum,
			)
		}
		if (!studentQuery) return filtered
		return filtered.filter(student => {
			const searchable =
				`${getStudentName(student)} ${student.meta || ''} ${student.grade || ''}`.toLowerCase()
			return searchable.includes(studentQuery)
		})
	}, [students, studentPickerGrade, studentQuery])

	const openStudentPicker = useCallback(
		(origin, step) => {
			const hasSelectedStudent = Boolean(selectedStudentId)
			setPickerOrigin(origin)
			setPendingStudentId(selectedStudentId)
			setStudentSearch('')
			setStudentPickerGrade('all')
			setSendStep(
				step ??
					(origin !== 'header' && hasSelectedStudent ? 'preview' : 'student'),
			)
			setIsSendOpen(true)
		},
		[selectedStudentId],
	)

	const closeSendModal = useCallback(() => {
		setIsSendOpen(false)
	}, [])

	const selectRecentStudent = useCallback(
		studentId => {
			applySelectedStudentId(studentId)
			setPendingStudentId(String(studentId))
		},
		[applySelectedStudentId],
	)

	useEffect(() => {
		researchSessionStore.ensureSessionReady().catch(() => {})
	}, [])

	// Seed the session course multi-select from the Profile (hydrate / Resume).
	useEffect(() => {
		if (!draftCourse) return
		setSelectedCourses(current =>
			current.includes(draftCourse) ? current : [draftCourse],
		)
	}, [draftCourse])

	useEffect(() => {
		courseMapStore.ensureLoaded(activeDegree).catch(() => {})
	}, [activeDegree])

	useEffect(() => {
		if (!isSendOpen) return
		ensureStudentsLoaded().catch(error => {
			console.error('Failed to load students for research:', error)
		})
	}, [isSendOpen])

	const isFinder = activeScreen !== 'main'
	const finderTitle = FINDER_TITLES[activeScreen] || 'Research'
	useEffect(() => {
		if (!isFinder) {
			setHeaderLeading?.(null)
			return undefined
		}
		setHeaderLeading?.(
			<div className={headerStyles.headerFinderTitle}>
				<button
					type="button"
					className={headerStyles.headerBackButton}
					onClick={() => setActiveScreen('main')}
					aria-label="Back to Research tools"
				>
					<ArrowBackIcon className={headerStyles.headerBackIcon} />
				</button>
				<span className={headerStyles.headerTitle}>{finderTitle}</span>
			</div>,
		)
		return () => setHeaderLeading?.(null)
	}, [isFinder, setHeaderLeading, finderTitle])

	const headerStudentAction = useMemo(() => {
		return (
			<HeaderStudentMenu
				selectedId={selectedStudentId}
				selectedName={selectedStudentName}
				recentStudents={JSON.parse(recentStudentsKey)}
				onSelectRecent={selectRecentStudent}
				onSelectAll={() => openStudentPicker('header')}
			/>
		)
	}, [
		openStudentPicker,
		recentStudentsKey,
		selectRecentStudent,
		selectedStudentId,
		selectedStudentName,
	])

	useHeaderActions(headerStudentAction)

	const clearSelectedCountries = useCallback(async () => {
		const persistedCountry =
			researchSessionStore.draftDefaults?.country || draftCountry || ''
		await persistFinderSelection(
			'country',
			{
				country: persistedCountry,
				countries: [],
			},
			{
				nextCountry: persistedCountry,
				nextCountries: [],
				nextCareer: researchSessionStore.draftDefaults?.career || '',
				nextCareers: careerTitlesFromIds(careerState.selectedIds),
				isSelectedCareer,
			},
		)
		setIsSelectedCountry(false)
	}, [
		draftCountry,
		persistFinderSelection,
		careerTitlesFromIds,
		careerState.selectedIds,
		isSelectedCareer,
	])

	const clearSelectedCourses = useCallback(() => {
		setSelectedCourses([])
		setIsSelectedCourse(false)
	}, [])

	const clearSelectedUniversities = useCallback(() => {
		setSelectedUniversities([])
	}, [])

	const toggleUniversitySelection = useCallback(item => {
		const selectionId = getResearchUniversitySelectionId(item)
		if (!selectionId) return
		setSelectedUniversities(prev => {
			const exists = prev.some(
				row => getResearchUniversitySelectionId(row) === selectionId,
			)
			if (exists) {
				return prev.filter(
					row => getResearchUniversitySelectionId(row) !== selectionId,
				)
			}
			return [...prev, item]
		})
	}, [])

	const selectedUniversityIds = useMemo(
		() =>
			selectedUniversities
				.map(item => getResearchUniversitySelectionId(item))
				.filter(Boolean),
		[selectedUniversities],
	)

	const selectedCountryItems = useMemo(() => {
		if (!isSelectedCountry || !draftCountries.length) return []
		const byTitle = new Map(
			(countryState.countryCards || []).map(card => [card.title, card]),
		)
		return draftCountries.map(
			title =>
				byTitle.get(title) || {
					id: title,
					title,
					salary: '',
					jobs: '',
					immigrationSummary: '',
				},
		)
	}, [isSelectedCountry, draftCountries, countryState.countryCards])

	const selectedCourseItems = useMemo(() => {
		if (!isSelectedCourse || !selectedCourses.length) return []
		const byTitle = new Map(
			(courseState.courseCards || []).map(card => [card.title, card]),
		)
		return selectedCourses.map(
			title =>
				byTitle.get(title) || {
					id: title,
					title,
					tuition: '',
					duration: '',
					subjects: [],
				},
		)
	}, [isSelectedCourse, selectedCourses, courseState.courseCards])

	const trayItems = useMemo(() => {
		if (activeScreen === 'career') {
			if (!isSelectedCareer) return []
			return careerState.selectedCareers.map(careerItem => ({
				id: careerItem.id,
				label: careerItem.title,
			}))
		}
		if (activeScreen === 'country') {
			if (!isSelectedCountry) return []
			return draftCountries.map(title => ({ id: title, label: title }))
		}
		if (activeScreen === 'course') {
			if (!isSelectedCourse) return []
			return selectedCourses.map(title => ({ id: title, label: title }))
		}
		if (activeScreen === 'college') {
			return selectedUniversities.map(item => ({
				id: getResearchUniversitySelectionId(item),
				label: getUniversityRecommendationLabel(item),
			}))
		}
		return []
	}, [
		activeScreen,
		isSelectedCareer,
		careerState.selectedCareers,
		isSelectedCountry,
		draftCountries,
		isSelectedCourse,
		selectedCourses,
		selectedUniversities,
	])

	const showTray = useMemo(() => {
		if (activeScreen === 'career') {
			return isSelectedCareer && careerState.selectedCareers.length > 0
		}
		if (activeScreen === 'country') {
			return isSelectedCountry && draftCountries.length > 0
		}
		if (activeScreen === 'course') {
			return isSelectedCourse && selectedCourses.length > 0
		}
		if (activeScreen === 'college') {
			return selectedUniversities.length > 0
		}
		return false
	}, [
		activeScreen,
		isSelectedCareer,
		careerState.selectedCareers.length,
		isSelectedCountry,
		draftCountries.length,
		isSelectedCourse,
		selectedCourses.length,
		selectedUniversities.length,
	])

	const onTrayRemove = useCallback(
		id => {
			if (selectionPending) return
			if (activeScreen === 'career') {
				toggleCareerCard(id)
				return
			}
			if (activeScreen === 'country') {
				toggleDraftCountry(id)
				return
			}
			if (activeScreen === 'course') {
				toggleDraftCourse(id)
				return
			}
			if (activeScreen === 'college') {
				setSelectedUniversities(prev =>
					prev.filter(item => getResearchUniversitySelectionId(item) !== id),
				)
			}
		},
		[
			selectionPending,
			activeScreen,
			toggleCareerCard,
			toggleDraftCountry,
			toggleDraftCourse,
		],
	)

	const onTrayClear = useCallback(() => {
		if (selectionPending) return
		if (activeScreen === 'career') {
			careerState.clearSelectedCareers()
			setIsSelectedCareer(false)
			return
		}
		if (activeScreen === 'country') {
			clearSelectedCountries()
			return
		}
		if (activeScreen === 'course') {
			clearSelectedCourses()
			return
		}
		if (activeScreen === 'college') {
			clearSelectedUniversities()
		}
	}, [
		selectionPending,
		activeScreen,
		careerState,
		clearSelectedCountries,
		clearSelectedCourses,
		clearSelectedUniversities,
	])

	const useStudent = () => {
		const ok = applySelectedStudentId(pendingStudentId)
		if (ok) setIsSendOpen(false)
	}

	const previewRecommendation = () => {
		const ok = applySelectedStudentId(pendingStudentId)
		if (ok) setSendStep('preview')
	}

	const sendRecommendation = useCallback(
		async ({
			intro,
			includeCareers = false,
			includeCountries = false,
			includeCourses = false,
			includeUniversities = false,
		} = {}) => {
			const studentId = selectedStudentId || pendingStudentId
			if (!studentId) {
				throw new Error('Select a student before sending.')
			}

			const careers = includeCareers ? careerState.selectedCareers : []
			const countries = includeCountries ? selectedCountryItems : []
			const courses = includeCourses ? selectedCourseItems : []
			const universities = includeUniversities ? selectedUniversities : []

			if (
				!careers.length &&
				!countries.length &&
				!courses.length &&
				!universities.length
			) {
				throw new Error('Select at least one recommendation block to send.')
			}

			const message = buildRecommendationMessageText({
				intro,
				includeCareers: careers.length > 0,
				includeCountries: countries.length > 0,
				includeCourses: courses.length > 0,
				includeUniversities: universities.length > 0,
				careers,
				countries,
				courses,
				universities,
			})

			const metadata = buildRecommendationMetadata({
				bodyText: intro,
				includeCareers: careers.length > 0,
				includeCountries: countries.length > 0,
				includeCourses: courses.length > 0,
				includeUniversities: universities.length > 0,
				careers,
				countries,
				courses,
				universities,
			})

			await sendMessagesToStudents({
				message,
				student_ids: [String(studentId)],
				metadata,
			})

			if (includeCareers) careerState.clearSelectedCareers()
			if (includeCountries) await clearSelectedCountries()
			if (includeCourses) clearSelectedCourses()
			if (includeUniversities) clearSelectedUniversities()

			careerState.setSentMessage(
				`Recommendation sent to ${getStudentName(
					selectedStudent || pendingStudent,
				)}`,
			)
			setIsSendOpen(false)
			setSendStep('student')
		},
		[
			selectedStudentId,
			pendingStudentId,
			careerState,
			selectedCountryItems,
			selectedCourseItems,
			selectedUniversities,
			clearSelectedCountries,
			clearSelectedCourses,
			clearSelectedUniversities,
			selectedStudent,
			pendingStudent,
		],
	)

	return {
		TOOLS,
		getStudentName,
		professions: careerState.professions,
		loadingProfession: careerState.loadingProfession,
		professionsError: careerState.professionsError,
		resetProfessions: careerState.resetProfessions,
		retryProfessions: careerState.retryProfessions,
		countries: countryState.countries,
		countryCards: countryState.countryCards,
		loadingCountries: countryState.loadingCountries,
		countriesError: countryState.countriesError,
		resetCountries: countryState.resetCountries,
		retryCountries: countryState.retryCountries,
		courses: courseState.courses,
		courseCards: courseState.courseCards,
		loadingCourses: courseState.loadingCourses,
		coursesError: courseState.coursesError,
		resetCourses: courseState.resetCourses,
		retryCourses: courseState.retryCourses,
		retryCourseMap,
		courseMapError: courseMapStore.error,
		courseMapLoading: courseMapStore.loading,
		careers: careerState.careers,
		career,
		draftCountry,
		draftCountries,
		draftCourse,
		selectedCourses,
		isSelectedCountry,
		isSelectedCourse,
		isSelectedCareer,
		recCountry: countryState.recCountry,
		activeDegree,
		selectDraftCareer: selectDraftCareerFromFilter,
		toggleDraftCountry,
		toggleDraftCourse,
		selectDraftDegree,
		onCategoryOrSubCategoryCommitted,
		pendingSwitchStudentId,
		pendingSwitchStudentName,
		keepCurrentResearchOnSwitch,
		loadPendingStudentOnSwitch,
		activeScreen,
		setActiveScreen,
		onExploreSelect,
		onStepperSelect,
		onResumeJourney,
		onViewStudentJourney,
		onDeleteJourney,
		journeys: researchSessionStore.journeys,
		journeysStatus: researchSessionStore.journeysStatus,
		studentAitoolsJourneys: researchSessionStore.studentAitoolsJourneys,
		studentAitoolsJourneysStatus:
			researchSessionStore.studentAitoolsJourneysStatus,
		activeJourneyId: researchSessionStore.activeJourneyId,
		query: careerState.query,
		setQuery: careerState.setQuery,
		role: careerState.role,
		setRole: careerState.setRole,
		selectedIds: careerState.selectedIds,
		selectedCareers: careerState.selectedCareers,
		filteredCareers: careerState.filteredCareers,
		isSendOpen,
		sendStep,
		setSendStep,
		pickerOrigin,
		categoryPickerOpen,
		categoryPickerStep,
		pendingStudentId,
		setPendingStudentId,
		studentSearch,
		setStudentSearch,
		studentPickerGrade,
		setStudentPickerGrade,
		studentGradeTabs,
		sentMessage: careerState.sentMessage,
		draftCategory,
		categoryDisplayLabel,
		subCategoryDisplayLabel,
		students,
		selectedStudent,
		pendingStudent,
		selectedStudentName,
		selectedStudentId,
		studentQuery,
		visibleStudents,
		toggleCareer: toggleCareerCard,
		clearSelectedCareers: careerState.clearSelectedCareers,
		selectionPending,
		pendingSelectionId,
		openCategoryPicker,
		closeCategoryPicker,
		openStudentPicker,
		closeSendModal,
		selectRecentStudent,
		useStudent,
		previewRecommendation,
		sendRecommendation,
		showTray,
		trayItems,
		onTrayRemove,
		onTrayClear,
		selectedCountryItems,
		selectedCourseItems,
		selectedUniversities,
		selectedUniversityIds,
		toggleUniversitySelection,
		recommendationKind:
			activeScreen === 'college' ? 'universities' : 'finders',
		universityNewData: universityState.newData,
		universitySortOptions: universityState.sortOptions,
		universitySortBy: universityState.sortBy,
		universitySortOrder: universityState.sortOrder,
		shortCards: universityState.shortCards,
		loadingUniversities: universityState.loadingUniversities,
		universitiesError: universityState.universitiesError,
		universitySearchDisabled: universityState.searchDisabled,
		setUniversityNewData: universityState.setNewData,
		setUniversitySearchDisabled: universityState.setSearchDisabled,
		universityNoShortlist: universityState.noShortlist,
		universityShowPlaceholders: universityState.showPlaceholders,
		universitySortDisabled: universityState.isDisableSortBtn,
		universityShowMoreDisabled: universityState.isButtonDisabled,
		onUniversitySearch: universityState.onSearch,
		onUniversitySortChange: universityState.onSortChange,
		onUniversityShowMore: universityState.onShowMore,
		retryUniversities: universityState.retryUniversities,
		resetUniversities: universityState.resetUniversities,
		universityIsFetched: universityState.isFetched,
	}
}
